/**
 * "Catalogue" on a customer's Settings tab: how the customer's catalogue is
 * kept (v1 completion F §4.3). Owned by the catalogue, not credit control, so
 * it loads and saves on its own: the switch saves at once, outside the page's
 * Save bar. Package O's "Ordering" section will sit beside it.
 *
 * Hidden for a caller who may not see the customer's catalogue (the read
 * answers 403 or 404).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ApiError } from "@shared-core/data/api/errors";
import type { CustomerCatalogueSettings } from "@shared-core/data/catalogue-types";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Label } from "../../primitives/label";
import { Skeleton } from "../../primitives/skeleton";
import { Switch } from "../../primitives/switch";

type Load =
  | { kind: "loading" }
  | { kind: "hidden" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; settings: CustomerCatalogueSettings };

export function CustomerCatalogueSettingsSection({
  customerCode,
}: {
  customerCode: string;
}) {
  const adapter = useDataAdapter();
  const [load, setLoad] = useState<Load>({ kind: "loading" });
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => {
    setLoad({ kind: "loading" });
    adapter
      .getCustomerCatalogueSettings(customerCode)
      .then((settings) => setLoad({ kind: "loaded", settings }))
      .catch((err: unknown) => {
        if (
          err instanceof ApiError &&
          (err.status === 403 || err.status === 404)
        ) {
          setLoad({ kind: "hidden" });
          return;
        }
        setLoad({
          kind: "failed",
          message: mutationErrorMessage(err, "the server did not answer"),
        });
      });
  }, [adapter, customerCode]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (load.kind === "hidden") return null;

  const change = async (required: boolean) => {
    setSaving(true);
    try {
      const settings = await adapter.setCustomerProductRefRequired(
        customerCode,
        required,
      );
      setLoad({ kind: "loaded", settings });
      toast.success(
        required
          ? "New catalogue items now need the customer's product code."
          : "New catalogue items no longer need the customer's product code.",
      );
    } catch (err) {
      toast.error(
        `Not saved: ${mutationErrorMessage(err, "the server did not answer")}`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      aria-labelledby="catalogue-settings-heading"
      className="space-y-4 rounded-lg border border-border p-4 text-sm"
    >
      <h2 id="catalogue-settings-heading" className="text-base font-medium">
        Catalogue
      </h2>
      {load.kind === "loading" && (
        <div role="status" aria-label="Loading catalogue settings">
          <Skeleton className="h-4 w-1/2" />
        </div>
      )}
      {load.kind === "failed" && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            Could not load catalogue settings: {load.message}{" "}
            <button type="button" className="underline" onClick={reload}>
              Try again
            </button>
          </AlertDescription>
        </Alert>
      )}
      {load.kind === "loaded" && (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Switch
              id="catalogue-requires-customer-product-ref"
              checked={load.settings.requiresCustomerProductRef}
              disabled={saving || !load.settings.canManage}
              aria-describedby="catalogue-requires-customer-product-ref-help"
              onCheckedChange={(checked) => void change(checked)}
            />
            <Label
              htmlFor="catalogue-requires-customer-product-ref"
              className="font-normal"
            >
              Require customer product reference
            </Label>
          </div>
          <p
            id="catalogue-requires-customer-product-ref-help"
            className="text-xs text-muted-foreground"
          >
            When on, every new catalogue item for this customer needs the
            customer&apos;s own product code. Items already in the catalogue are
            unchanged.
          </p>
          {!load.settings.canManage && (
            <p className="text-xs text-muted-foreground">
              Managers and admins change this.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
